import React, { useEffect, useState } from 'react';
import { Box, Grid, IconButton, Tooltip } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { ContentCopy, TextSnippet } from '@mui/icons-material';
import { Column } from 'primereact/column';
import SettingsIcon from '@mui/icons-material/Settings';
import moment from 'moment';
import { toast } from 'react-toastify';
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';
import { RightSideBar } from '../../../../components/layout/rightSideBar/rightSideBar';
import { viewRightSideBarEnum } from '../../../../constants/sidebarEnums';
import { LoadingRowsComponent } from '../../../../components/commons/LoadingRows/LoadingRows';
import { useGetDeviceByLocationMutation } from '../../../../services/deviceByLocation/deviceByLocationService';
import { SendStaticOrSingletonMethod } from '../../../device/sendStaticOrSingleMethod/sendStaticOrSingleMethod';
import { AddDeviceBar } from '../../../device/addDeviceBar/addDeviceBar';
import { DetailsDevice } from '../../../device/detailsDevice/detailsDevice';
import { DeviceByLocation } from '../../../../types/Device/DeviceInterfaces';
import { DeviceListByLocationProps } from './deviceListByLocationProps';
import { MultipleDevicesSelectedMethods } from '../../../device/multipleDeviceSelectedMethods/multipleDevicesSelectedMethods';
import { SetWifiCredentialsComponent } from '../setWifiCredentials/setWifiCredentials';
import { SendDirectFOTA } from '../../../device/directFOTA/sendDirectFOTA';
import { AddNoteSideBar } from '../../../../components/commons/notes/addNoteSideBar/addNoteSideBar';
import Strings from '../../../../i18n';
import { DeleteButtonWithoutText } from '../../../../components/commons/DeleteButtonWithoutText/deleteButtonWithoutText';
import { useDeleteScheduleFirmwareUpdateMutation } from '../../../../services/firmwareVersions/firmwareVersionsService';
import { StyledButtonName } from '../../allLocationsList/allLocationListStyles';
import {
  StyledIconStatus,
  StyledStatusDevice
} from '../../../device/devicesTemplate/devicesTemplateStyle';
import { MoveDeviceToLocation } from '../../../device/moveDeviceToLocation/moveDeviceToLocation';
import { D2cMessages } from '../../../device/dcMessages/dcMessages';
import { SendDirectMethod } from '../../../device/sendDirectMethod/sendDirectMethod';
import { SendProdCommands } from '../../../device/sendProdCommands/sendProdCommands';
import { colors } from '../../../../constants/colors';
import { syncStatusEnum } from '../../../../constants/enums/deviceEnums';
import { PieChartStatus } from '../../../../constants/enums/dashboardEnums';
import { ContainerTable } from '../../locationsList/locationListStyles';
import { SetProFlagSideBar } from '../../../device/setProFlagBar/setProFlagSideBar';
import { GetDeferEventTime } from '../../../device/getDeferEventTime/getDeferEventTime';
import { SetDeferEventTime } from '../../../device/setDeferEventTime/setDeferEventTime';

const flagIcon = require('../../../../assets/proImage.png');

export const DeviceListByLocationTemplate = ({
  searchValue,
  openRightSideBar,
  setOpenRightSideBar,
  selectedDevices,
  setSelectedDevices,
  sideBarOpenFor,
  setSideBarOpenFor,
  refreshDevices,
  setRefreshDevices,
  setOnlineAndTotalDevices,
  accessPointId
}: DeviceListByLocationProps) => {
  const { locationId } = useParams();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const searchQuery = queryParams.get('search');
  const [reorderedData, setReorderedData] = useState<DeviceByLocation[]>([]);
  const navigate = useNavigate();
  const openRightSideBarForMethod = (method: number) => {
    setSideBarOpenFor(method);
    setOpenRightSideBar(true);
  };

  const [getDevices, { isSuccess, data, isLoading }] =
    useGetDeviceByLocationMutation();
  const clearSelectedDevices = () => {
    setSelectedDevices([]);
  };

  const [
    deleteScheduledFirmware,
    { isSuccess: isSuccessDeleteScheduledFirmware }
  ] = useDeleteScheduleFirmwareUpdateMutation();

  const reloadTable = () => {
    if (locationId) {
      getDevices(+locationId);
      clearSelectedDevices();
    }
  };

  useEffect(() => {
    reloadTable();
  }, [locationId]);

  useEffect(() => {
    if (refreshDevices) {
      reloadTable();
      setRefreshDevices(false);
    }
  }, [refreshDevices]);

  useEffect(() => {
    if (data) {
      setOnlineAndTotalDevices({
        online: data.filter(
          (item) =>
            item.connectionStatus === PieChartStatus.onlineLessThan15 ||
            item.connectionStatus === PieChartStatus.onlineMoreThan15
        ).length,
        total: data.length
      });
    }
  }, [data]);

  const switchComponentToShowRightSideBar = () => {
    switch (sideBarOpenFor) {
      case viewRightSideBarEnum.singletonDevice:
        return (
          <SendStaticOrSingletonMethod
            deviceIds={selectedDevices.map((item) => item.deviceId)}
            reloadTable={async () => reloadTable()}
            method={viewRightSideBarEnum.singletonDevice}
            setOpenRightSideBar={setOpenRightSideBar}
            clearSelectedDevices={clearSelectedDevices}
          />
        );
      case viewRightSideBarEnum.staticDevice:
        return (
          <SendStaticOrSingletonMethod
            deviceIds={selectedDevices.map((item) => item.deviceId)}
            reloadTable={async () => reloadTable()}
            method={viewRightSideBarEnum.staticDevice}
            setOpenRightSideBar={setOpenRightSideBar}
            clearSelectedDevices={clearSelectedDevices}
          />
        );
      case viewRightSideBarEnum.addDevice:
        return (
          <AddDeviceBar
            openRightSideBarForMethod={openRightSideBarForMethod}
            reloadTable={async () => reloadTable()}
            device={selectedDevices[0]}
            setOpenRightSideBar={setOpenRightSideBar}
            openRightSideBar={openRightSideBar}
            clearSelectedDevices={clearSelectedDevices}
          />
        );
      case viewRightSideBarEnum.setProFlag:
        return (
          <SetProFlagSideBar
            devicesIds={selectedDevices.map((item) => item.deviceId)}
            reloadTable={async () => reloadTable()}
            setOpenRightSideBar={setOpenRightSideBar}
            clearSelectedItems={clearSelectedDevices}
          />
        );
      case viewRightSideBarEnum.details:
        return (
          <DetailsDevice
            openRightSideBarForMethod={openRightSideBarForMethod}
            reloadTable={async () => reloadTable()}
            device={selectedDevices[0]}
            setOpenRightSideBar={setOpenRightSideBar}
            openRightSideBar={openRightSideBar}
            deviceLocationView
          />
        );
      case viewRightSideBarEnum.editMultipleDevices:
        return (
          <MultipleDevicesSelectedMethods
            reloadTable={() => reloadTable()}
            openRightSideBarForMethod={openRightSideBarForMethod}
            selectedDevice={selectedDevices}
            openRightSideBar={openRightSideBar}
            setOpenRightSideBar={setOpenRightSideBar}
            deviceLocationView
            clearSingleDevice={() => {}}
          />
        );
      case viewRightSideBarEnum.SetWifiCredentials:
        return (
          <SetWifiCredentialsComponent
            deviceIds={selectedDevices.map((item) => item.deviceId)}
            locationIds={selectedDevices.map((item) => item.locationId)}
            reloadTable={() => reloadTable()}
            setOpenRightSideBar={setOpenRightSideBar}
            clearSelectedDevices={clearSelectedDevices}
            openRightSideBarForMethod={openRightSideBarForMethod}
          />
        );
      case viewRightSideBarEnum.sendDirectMethodText:
        return (
          <SendDirectMethod
            deviceIds={selectedDevices.map((item) => item.deviceId)}
            setOpenRightSideBar={setOpenRightSideBar}
            clearSelectedDevices={clearSelectedDevices}
          />
        );
      case viewRightSideBarEnum.productionCommands:
        return (
          <SendProdCommands
            deviceIds={selectedDevices.map((item) => item.deviceId)}
            setOpenRightSideBar={setOpenRightSideBar}
            clearSelectedDevices={clearSelectedDevices}
          />
        );
      case viewRightSideBarEnum.setDeferEventTime:
        return (
          <SetDeferEventTime
            deviceIds={selectedDevices.map((item) => item.deviceId)}
            setOpenRightSideBar={setOpenRightSideBar}
            clearSelectedDevices={clearSelectedDevices}
          />
        );
      case viewRightSideBarEnum.getDeferEventTime:
        return (
          <GetDeferEventTime
            deviceIds={selectedDevices.map((item) => item.deviceId)}
            setOpenRightSideBar={setOpenRightSideBar}
            clearSelectedDevices={clearSelectedDevices}
          />
        );
      case viewRightSideBarEnum.directFOTA:
        return (
          <SendDirectFOTA
            deviceIds={selectedDevices.map((item) => ({
              deviceId: item.deviceId,
              productTypeId: item.productTypeId
            }))}
            reloadTable={async () => reloadTable()}
            clearSelectedDevices={clearSelectedDevices}
            setOpenRightSideBar={setOpenRightSideBar}
          />
        );
      case viewRightSideBarEnum.moveLocation:
        return (
          <MoveDeviceToLocation
            reloadTable={reloadTable}
            ids={selectedDevices.map((item) => item.deviceId)}
            setOpenRightSideBar={setOpenRightSideBar}
            clearSelectedDevices={clearSelectedDevices}
          />
        );
      case viewRightSideBarEnum.addNote:
        return (
          <AddNoteSideBar
            closeSideBar={() => setOpenRightSideBar(false)}
            targetId={selectedDevices[0].deviceId}
            type="device"
          />
        );
      case viewRightSideBarEnum.d2cMessages: {
        const id =
          selectedDevices[0] && selectedDevices[0].deviceId
            ? selectedDevices[0].deviceId
            : null;

        return (
          <D2cMessages
            key={id}
            clearSelectedDevices={clearSelectedDevices}
            id={id}
            openSideBar={openRightSideBar}
          />
        );
      }
      default:
        return (
          <AddDeviceBar
            reloadTable={async () => reloadTable()}
            openRightSideBarForMethod={openRightSideBarForMethod}
            openRightSideBar={openRightSideBar}
            setOpenRightSideBar={setOpenRightSideBar}
            clearSelectedDevices={clearSelectedDevices}
          />
        );
    }
  };

  const filterData = () => {
    setReorderedData(
      data?.filter((item) => {
        const searchValueLowerCase = searchValue.toLowerCase();

        let includesSearchValue = false;

        includesSearchValue = item.deviceId
          .toLowerCase()
          .includes(searchValueLowerCase);

        if (accessPointId) {
          includesSearchValue = item.locationAccessPointId === accessPointId;
        }

        return includesSearchValue;
      }) ?? []
    );
  };

  useEffect(() => {
    if (sideBarOpenFor === viewRightSideBarEnum.d2cMessages) return;

    if (selectedDevices.length === 1) {
      openRightSideBarForMethod(viewRightSideBarEnum.details);
    } else if (selectedDevices.length > 1) {
      openRightSideBarForMethod(viewRightSideBarEnum.editMultipleDevices);
    } else {
      openRightSideBarForMethod(viewRightSideBarEnum.empty);
      setOpenRightSideBar(false);
    }
  }, [selectedDevices]);

  const reorderDevices = (deviceList: DeviceByLocation[], idToMove: string) => {
    const usersArrayCopy = [...deviceList];
    return usersArrayCopy.sort((a, b) => {
      if (a.deviceId === idToMove) return -1;
      if (b.deviceId === idToMove) return 1;
      return 0;
    });
  };
  useEffect(() => {
    if (searchQuery) {
      setOpenRightSideBar(false);
      setSelectedDevices(
        data?.filter((item) => item.deviceId === searchQuery) ?? []
      );
      setReorderedData(reorderDevices(data ?? [], searchQuery));
    } else {
      setReorderedData(data ?? []);
    }
    filterData();
  }, [isSuccess, searchQuery]);

  const getDatabaseLocal = (rowData: DeviceByLocation) => {
    if (rowData.dataBaseStatus) {
      const utcDate = moment.utc(rowData.lastUpdate);
      const browserTime = utcDate.local();
      return (
        <Grid container xs={12}>
          <Tooltip title={Strings.common.timeline} placement="top" arrow>
            <IconButton
              sx={{ zIndex: 1000 }}
              size="small"
              onClick={() => {
                navigate(`${rowData.deviceId}/timeline`);
              }}
            >
              <ViewTimelineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <StyledStatusDevice status={rowData.connectionStatus}>
            {rowData.statusLabel}
          </StyledStatusDevice>
        </Grid>
      );
    }
    return (
      <IconButton
        sx={{ zIndex: 1000 }}
        size="small"
        onClick={() => {
          navigate(`${rowData.deviceId}/timeline`);
        }}
      >
        <ViewTimelineIcon fontSize="small" />
      </IconButton>
    );
  };

  useEffect(() => {
    if (isSuccessDeleteScheduledFirmware) {
      reloadTable();
      toast.success(Strings.serverAccess.scheduleFirmwareUpdate.deleteSuccess);
    }
  }, [isSuccessDeleteScheduledFirmware]);

  const getTargetWithDeleteButton = (rowData: DeviceByLocation) => {
    return rowData.firmwareUpdate ? (
      <>
        <DeleteButtonWithoutText
          onDelete={() => {
            deleteScheduledFirmware([
              {
                id: rowData.firmwareUpdate.firmwareScheduleId,
                firmwareversion: rowData.firmwareUpdate.firmwareTarget
              }
            ]);
          }}
          id={rowData.id.toString()}
          titleConfirmation={
            Strings.serverAccess.scheduleFirmwareUpdate.deleteFirmwareTitle
          }
          textConfirmation={
            Strings.serverAccess.scheduleFirmwareUpdate.deleteFirmware
          }
        />
        {rowData.firmwareUpdate.firmwareTarget}
      </>
    ) : (
      <span />
    );
  };
  const redirectToNotes = (item: DeviceByLocation) => {
    navigate('/my-notes', {
      state: {
        type: 'Devices',
        id: item.deviceId
      }
    });
  };

  const getDeviceId = (item: DeviceByLocation) => {
    return (
      <Grid container xs={12}>
        {item.hasNotes && (
          <Tooltip title={Strings.common.notes} placement="top" arrow>
            <IconButton size="small" onClick={() => redirectToNotes(item)}>
              <TextSnippet fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={Strings.common.copyText} placement="top" arrow>
          <IconButton
            sx={{ zIndex: 1000 }}
            size="small"
            onClick={() => {
              navigator.clipboard.writeText(item.deviceId);
              toast.success(Strings.common.copiedText);
            }}
          >
            <ContentCopy fontSize="small" />
          </IconButton>
        </Tooltip>
        <StyledButtonName>{item.deviceId}</StyledButtonName>
      </Grid>
    );
  };

  const getDeviceName = (item: DeviceByLocation) => {
    return (
      <Grid container xs={12}>
        <Tooltip title={Strings.common.seeD2C} placement="top" arrow>
          <IconButton
            size="small"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();

              setSideBarOpenFor(viewRightSideBarEnum.d2cMessages);
              setOpenRightSideBar(true);
              setSelectedDevices([item]);
            }}
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <StyledButtonName>{item.name}</StyledButtonName>
      </Grid>
    );
  };

  const getSignal = (item: DeviceByLocation) => {
    return (
      <Grid
        container
        xs={12}
        justifyContent="center"
        alignContent="center"
        alignItems="center"
      >
        <span>{item.controllersSignalStrength}</span>
      </Grid>
    );
  };

  const getFirmware = (item: DeviceByLocation) => {
    const xSeriesGen = 7;
    return (
      <Grid
        container
        xs={12}
        justifyContent="center"
        alignContent="center"
        alignItems="center"
      >
        <span>
          {item.firmwareVersion}
          {item.generation < xSeriesGen && ` (${item.wiFiFirmwareVersion})`}
        </span>
      </Grid>
    );
  };
  const getDetailsSyncStatus = (details: any) => {
    const syncIssues = Object.keys(details).reduce(
      (acc: string[], key: string) => {
        if (details[key] === false) {
          acc.push(`${key}: ${Strings.devices.notSynced}`);
        } else if (details[key] === null) {
          acc.push(`${key}: ${Strings.devices.na}`);
        }
        return acc;
      },
      []
    );
    return syncIssues.length > 0
      ? syncIssues.join(', ')
      : Strings.devices.allSynced;
  };
  const getSyncStatus = (rowData: DeviceByLocation) => {
    switch (rowData.syncStatus.status) {
      case syncStatusEnum.synched:
        return (
          <Tooltip title={Strings.devices.allSynced}>
            <StyledIconStatus
              color={colors.green}
              className="pi pi-check-circle"
            />
          </Tooltip>
        );
      case syncStatusEnum.notSynched:
        return (
          <Tooltip title={getDetailsSyncStatus(rowData.syncStatus.details)}>
            <StyledIconStatus
              color={colors.red}
              className="pi pi-times-circle"
            />
          </Tooltip>
        );
      case syncStatusEnum.noInfo:
        return (
          <Tooltip title={Strings.devices.na}>
            <StyledIconStatus
              color={colors.mediumGray}
              className="pi pi-question-circle"
            />
          </Tooltip>
        );
      default:
        return '';
    }
  };

  const getProFlag = (rowData: DeviceByLocation) => {
    return rowData.proFlag ? (
      <img src={flagIcon} alt="Pro Flag" width="30px" />
    ) : (
      ''
    );
  };

  const getDeferCommand = (rowData: DeviceByLocation) => {
    if (rowData.deviceEventDeferCommandSent) {
      const { deferOnUntilDate: onUntilDate, deferOffUntilDate: offUntilDate } =
        rowData.deviceEventDeferCommandSent;
      const today = moment();

      const dates = [
        onUntilDate && moment(onUntilDate).isAfter(today)
          ? `On until: ${moment(onUntilDate).format('DD/MM/YYYY')}`
          : null,
        offUntilDate && moment(offUntilDate).isAfter(today)
          ? `Off until: ${moment(offUntilDate).format('DD/MM/YYYY')}`
          : null
      ].filter(Boolean);

      return dates.length > 0 ? <span>{dates.join(' ')}</span> : '-';
    }

    return '-';
  };

  return (
    <ContainerTable component="main">
      {isLoading && <LoadingRowsComponent />}
      {isSuccess && reorderedData && (
        <Box>
          <DataTable
            selection={selectedDevices}
            onSelectionChange={(e) => {
              if (e.value.length === 0) {
                setSideBarOpenFor(viewRightSideBarEnum.addDevice);
                setOpenRightSideBar(false);
              }
              setSelectedDevices(e.value);
            }}
            selectionMode="multiple"
            metaKeySelection
            value={reorderedData}
            size="small"
            scrollable
            scrollHeight="flex"
            paginator={reorderedData && reorderedData.length >= 50}
            selectionPageOnly
            rows={50}
            rowsPerPageOptions={[
              5,
              10,
              25,
              reorderedData ? reorderedData.length : 50
            ]}
            tableStyle={{ fontSize: '14px' }}
          >
            <Column selectionMode="multiple" frozen />
            <Column field="proFlag" header="" sortable body={getProFlag} />
            <Column
              field="statusLabel"
              header="Online / Offline"
              sortable
              body={getDatabaseLocal}
            />
            <Column field="name" header="Name" sortable body={getDeviceName} />
            <Column
              field="deviceId"
              header="Controller ID"
              sortable
              body={getDeviceId}
            />
            <Column
              field="controllersSignalStrength"
              header="Signal strength"
              sortable
              body={getSignal}
            />
            <Column field="productType" header="Model" sortable />
            <Column
              field="firmwareVersion"
              header="Firmware Version "
              sortable
              body={getFirmware}
            />
            <Column
              field="firmwareUpdate.firmwareTarget"
              header="Pending FOTA"
              sortable
              body={getTargetWithDeleteButton}
            />
            <Column
              field="deviceEventDeferCommandSent"
              header="Defer Command"
              sortable
              body={getDeferCommand}
            />
            <Column
              field="syncStatus.status"
              header="Sync Status"
              sortable
              body={getSyncStatus}
            />
            <Column
              field="eventVersionReported"
              header="Table Status"
              sortable
            />
          </DataTable>
        </Box>
      )}

      <RightSideBar
        open={openRightSideBar}
        onMenuClose={() => {
          setOpenRightSideBar(false);
          setSideBarOpenFor(viewRightSideBarEnum.addDevice);
          setSelectedDevices([]);
        }}
      >
        {switchComponentToShowRightSideBar()}
      </RightSideBar>
    </ContainerTable>
  );
};
